import { getCleanSelectionText } from '@/utils/cleanSelection';
import { copyToClipboard } from '@/utils/clipboard';
import { CopyOutlined } from '@ant-design/icons';
import { useIntl } from '@umijs/max';
import { Button, message, theme } from 'antd';
import type { ReactNode } from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import './index.less';

type SelectionAction = { key: string; label: string; icon: ReactNode; onClick: () => void };
type ActionProvider = (text: string) => SelectionAction;
const actionProviders = new Set<ActionProvider>();

export const registerSelectionAction = (provider: ActionProvider) => {
  actionProviders.add(provider);
  return () => {
    actionProviders.delete(provider);
  };
};

type MenuSelection = { x: number; y: number; text: string; actions: SelectionAction[] };

const CleanTextContextMenu = () => {
  const intl = useIntl();
  const { token } = theme.useToken();
  const [menu, setMenu] = useState<MenuSelection | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const onContextMenu = (event: MouseEvent) => {
      setMenu(null);
      // Shift + right-click shows only the browser's usual actions.
      if (event.defaultPrevented || event.shiftKey) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      let text = '';
      let selectedText = '';
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
        if (target instanceof HTMLInputElement && target.type === 'password') return;
        text = target.value.slice(target.selectionStart ?? 0, target.selectionEnd ?? 0);
        selectedText = text;
      } else {
        const selection = window.getSelection();
        if (!selection || selection.isCollapsed || !selection.rangeCount) return;
        if (!selection.getRangeAt(0).intersectsNode(target)) return;
        selectedText = selection.toString();
        text = getCleanSelectionText(selection);
      }
      if (!text.trim()) return;
      previousFocus.current = document.activeElement as HTMLElement | null;
      // Commit our popup before the browser handles its native menu. Leave the
      // event uncancelled and preserve focus/selection for Chrome's own actions.
      const actions = Array.from(actionProviders, (provider) => provider(selectedText));
      flushSync(() => setMenu({ x: event.clientX, y: event.clientY, text, actions }));
    };
    document.addEventListener('contextmenu', onContextMenu);
    return () => document.removeEventListener('contextmenu', onContextMenu);
  }, []);

  useLayoutEffect(() => {
    if (!menu || !menuRef.current) return;
    const bounds = menuRef.current.getBoundingClientRect();
    menuRef.current.style.left = `${Math.max(
      8,
      Math.min(menu.x, window.innerWidth - bounds.width - 8),
    )}px`;
    menuRef.current.style.top = `${Math.max(
      8,
      Math.min(menu.y - bounds.height - 8, window.innerHeight - bounds.height - 8),
    )}px`;
  }, [menu]);

  useEffect(() => {
    if (!menu) return;
    const dismiss = () => setMenu(null);
    const onPointerDown = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) dismiss();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        previousFocus.current?.focus({ preventScroll: true });
        dismiss();
      } else if (event.key === 'Tab') {
        dismiss();
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const buttons = Array.from(menuRef.current?.querySelectorAll('button') ?? []);
        const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        const first = direction === 1 ? 0 : buttons.length - 1;
        const next = current < 0 ? first : (current + direction + buttons.length) % buttons.length;
        buttons[next]?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    // Opening a native context menu can move focus away from the page. Keep
    // our popup available until an explicit dismissal or the next page click.
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
    };
  }, [menu]);

  const copy = async () => {
    if (!menu) return;
    const text = menu.text;
    setMenu(null);
    previousFocus.current?.focus({ preventScroll: true });
    const onSuccess = (): void => {
      message.success(
        intl.formatMessage({
          id: 'components.cleanTextContextMenu.copied',
          defaultMessage: 'Clean text copied',
        }),
      );
    };
    const onError = (): void => {
      message.error(
        intl.formatMessage({
          id: 'components.cleanTextContextMenu.failed',
          defaultMessage: 'Could not copy text. Please try again.',
        }),
      );
    };
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        onSuccess();
      } else {
        copyToClipboard(text, { onSuccess, onError });
      }
    } catch {
      onError();
    }
  };

  if (!menu) return null;
  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      className="clean-text-context-menu"
      style={{
        left: menu.x,
        top: menu.y,
        zIndex: token.zIndexPopupBase + 100,
      }}
    >
      <Button
        role="menuitem"
        icon={<CopyOutlined aria-hidden="true" />}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => void copy()}
      >
        {intl.formatMessage({
          id: 'components.cleanTextContextMenu.copy',
          defaultMessage: 'Copy clean text',
        })}
      </Button>
      {menu.actions.map((action) => (
        <Button
          key={action.key}
          role="menuitem"
          icon={action.icon}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            setMenu(null);
            action.onClick();
          }}
        >
          {action.label}
        </Button>
      ))}
    </div>,
    document.body,
  );
};

export default CleanTextContextMenu;
